import { Prisma } from '@prisma/client/extension';
import { CascadeHandler } from './cascade-handler';
import { isCascadeConfigured, requireCascadeDmmf } from './dmmf-resolver';
import { applyRelationReadFilters } from './relation-filter';
import type {
  PrismaDmmfLike,
  RelationFilterOptions,
  SoftDeleteExtensionOptions,
} from '../interfaces/soft-delete-options.interface';
import { SoftDeleteContext } from '../services/soft-delete-context';
import { DEFAULT_DELETED_AT_FIELD, DEFAULT_MAX_CASCADE_DEPTH } from '../soft-delete.constants';
import { SoftDeletedEvent } from '../events/soft-delete.events';
import { getRegisteredSoftDeleteEventEmitter } from '../events/soft-delete-event-emitter';
import { RelationDmmfMissingError } from '../errors/relation-dmmf-missing.error';
import {
  DEFAULT_AUDIT_MAX_BATCH_RECORDS,
  runAuditLifecycle,
  validateAuditLifecycleOptions,
} from './audit-lifecycle';

/**
 * Determines whether a given model name is in the list of soft-delete models.
 * Comparison is case-insensitive to handle variations in casing.
 */
function isSoftDeleteModel(model: string, softDeleteModels: string[]): boolean {
  const normalized = model.toLowerCase();
  return softDeleteModels.some((m) => m.toLowerCase() === normalized);
}

/**
 * Builds the data payload for a soft-delete update operation.
 */
function buildSoftDeleteData(
  deletedAtField: string,
  deletedByField: string | null | undefined,
): Record<string, unknown> {
  const data: Record<string, unknown> = {
    [deletedAtField]: new Date(),
  };

  if (deletedByField) {
    const actorId = SoftDeleteContext.getActorId();
    if (actorId != null) {
      data[deletedByField] = actorId;
    }
  }

  return data;
}

/**
 * Injects the appropriate soft-delete filter into a where clause
 * based on the current filter mode from SoftDeleteContext.
 */
function applyReadFilter(
  where: Record<string, unknown> | undefined,
  deletedAtField: string,
): Record<string, unknown> {
  const filterMode = SoftDeleteContext.getFilterMode();

  if (filterMode === 'withDeleted') {
    return where ?? {};
  }

  const filter =
    filterMode === 'onlyDeleted' ? { [deletedAtField]: { not: null } } : { [deletedAtField]: null };

  if (!where) {
    return filter;
  }

  return { ...where, ...filter };
}

function applyActiveWriteFilter(
  where: Record<string, unknown> | undefined,
  deletedAtField: string,
): Record<string, unknown> {
  return { ...(where ?? {}), [deletedAtField]: null };
}

function findPrimaryKey(model: string, dmmf?: PrismaDmmfLike): string {
  const modelDef = dmmf?.datamodel.models.find((candidate: any) => candidate.name === model);
  const pkField = modelDef?.fields.find((field: any) => field.isId);
  return pkField?.name ?? 'id';
}

function resolveRelationFilterOptions(
  relationFilters: boolean | RelationFilterOptions | undefined,
): { enabled: boolean; maxDepth: number } {
  if (relationFilters === true) {
    return { enabled: true, maxDepth: 3 };
  }

  if (!relationFilters) {
    return { enabled: false, maxDepth: 3 };
  }

  return {
    enabled: relationFilters.enabled ?? true,
    maxDepth: relationFilters.maxDepth ?? 3,
  };
}

export interface SoftDeleteQueryHandlers {
  delete: (params: {
    model: string;
    args: any;
    query: (args: any) => Promise<unknown>;
    client: any;
  }) => Promise<unknown>;
  deleteMany: (params: {
    model: string;
    args: any;
    query: (args: any) => Promise<unknown>;
    client: any;
  }) => Promise<unknown>;
  findMany: (params: {
    model: string;
    args: any;
    query: (args: any) => Promise<unknown>;
  }) => Promise<unknown>;
  findFirst: (params: {
    model: string;
    args: any;
    query: (args: any) => Promise<unknown>;
  }) => Promise<unknown>;
  findUnique: (params: {
    model: string;
    args: any;
    query: (args: any) => Promise<unknown>;
  }) => Promise<unknown>;
  findFirstOrThrow: (params: {
    model: string;
    args: any;
    query: (args: any) => Promise<unknown>;
  }) => Promise<unknown>;
  findUniqueOrThrow: (params: {
    model: string;
    args: any;
    query: (args: any) => Promise<unknown>;
  }) => Promise<unknown>;
  count: (params: {
    model: string;
    args: any;
    query: (args: any) => Promise<unknown>;
  }) => Promise<unknown>;
  aggregate: (params: {
    model: string;
    args: any;
    query: (args: any) => Promise<unknown>;
  }) => Promise<unknown>;
  groupBy: (params: {
    model: string;
    args: any;
    query: (args: any) => Promise<unknown>;
  }) => Promise<unknown>;
}

/**
 * Builds the raw soft-delete query handler functions.
 * Exported for unit testing so handlers can be tested without
 * requiring a real Prisma client or Prisma.defineExtension.
 *
 * @param options - Soft delete extension configuration
 * @param dmmf - Optional Prisma DMMF metadata; required when cascade is configured
 */
export function _buildSoftDeleteQueryHandlers(
  options: SoftDeleteExtensionOptions,
  dmmf?: PrismaDmmfLike,
): SoftDeleteQueryHandlers {
  const deletedAtField = options.deletedAtField ?? DEFAULT_DELETED_AT_FIELD;
  const deletedByField = options.deletedByField ?? null;
  const softDeleteModels = options.softDeleteModels;
  const getEventEmitter = () => options.eventEmitter ?? getRegisteredSoftDeleteEventEmitter();
  const relationFilterOptions = resolveRelationFilterOptions(options.relationFilters);
  const relationFilterDmmf = options.dmmf ?? dmmf;

  validateAuditLifecycleOptions(options);

  if (relationFilterOptions.enabled && !relationFilterDmmf) {
    throw new RelationDmmfMissingError();
  }

  let cascadeHandler: CascadeHandler | null = null;
  if (isCascadeConfigured(options.cascade)) {
    const cascadeDmmf = requireCascadeDmmf({
      optionsDmmf: options.dmmf,
      fallbackDmmf: dmmf,
    });

    cascadeHandler = new CascadeHandler({
      cascade: options.cascade!,
      deletedAtField,
      deletedByField,
      maxCascadeDepth: options.maxCascadeDepth ?? DEFAULT_MAX_CASCADE_DEPTH,
      dmmf: cascadeDmmf,
    });
  }

  function createReadHandler(_operationName: string) {
    return async ({
      model,
      args,
      query,
    }: {
      model: string;
      args: any;
      query: (args: any) => Promise<unknown>;
    }): Promise<unknown> => {
      if (!isSoftDeleteModel(model, softDeleteModels) || SoftDeleteContext.isSkipped()) {
        return query(args);
      }

      const updatedArgs = { ...args };
      updatedArgs.where = applyReadFilter(updatedArgs.where, deletedAtField);

      const relationFilteredArgs =
        relationFilterOptions.enabled && relationFilterDmmf
          ? applyRelationReadFilters(updatedArgs, model, {
              dmmf: relationFilterDmmf,
              softDeleteModels,
              deletedAtField,
              maxDepth: relationFilterOptions.maxDepth,
            })
          : updatedArgs;

      return query(relationFilteredArgs);
    };
  }

  return {
    async delete({ model, args, query, client }) {
      if (!isSoftDeleteModel(model, softDeleteModels) || SoftDeleteContext.isSkipped()) {
        return query(args);
      }

      const data = buildSoftDeleteData(deletedAtField, deletedByField);
      const modelKey = model.charAt(0).toLowerCase() + model.slice(1);
      const result = await runAuditLifecycle(
        client,
        options,
        model,
        'delete',
        'softDeleted',
        async (mutationClient) => {
          const updated = await mutationClient[modelKey].update({
            where: applyActiveWriteFilter(args.where, deletedAtField),
            data,
          });
          if (cascadeHandler) {
            const pkField = cascadeHandler.findPrimaryKey(model);
            await cascadeHandler.cascadeSoftDelete(
              mutationClient,
              model,
              updated[pkField],
              data[deletedAtField] as Date,
              0,
              options.auditLifecycle === 'atomic-required'
                ? (childModel, callback) =>
                    runAuditLifecycle(
                      client,
                      options,
                      childModel,
                      'cascadeDelete',
                      'softDeleted',
                      callback,
                    )
                : undefined,
            );
          }
          return updated;
        },
        'delete',
      );

      getEventEmitter()?.emitSoftDeleted(
        new SoftDeletedEvent(
          model,
          args.where,
          data[deletedAtField] as Date,
          SoftDeleteContext.getActorId(),
          1,
        ),
      );

      return result;
    },

    async deleteMany({ model, args, query, client }) {
      if (!isSoftDeleteModel(model, softDeleteModels) || SoftDeleteContext.isSkipped()) {
        return query(args);
      }

      const data = buildSoftDeleteData(deletedAtField, deletedByField);
      const modelKey = model.charAt(0).toLowerCase() + model.slice(1);

      if (options.auditLifecycle === 'atomic-required') {
        const maxRecords = options.auditMaxBatchRecords ?? DEFAULT_AUDIT_MAX_BATCH_RECORDS;
        const result = await runAuditLifecycle(
          client,
          options,
          model,
          'deleteMany',
          'softDeleted',
          async (tx) => {
            const activeWhere = applyActiveWriteFilter(args.where, deletedAtField);
            const records = await tx[modelKey].findMany({
              where: activeWhere,
              take: maxRecords + 1,
            });
            if (records.length > maxRecords) {
              throw new Error(
                `[@nestarc/soft-delete] ${model}.deleteMany exceeds auditMaxBatchRecords (${maxRecords})`,
              );
            }
            const pkField =
              cascadeHandler?.findPrimaryKey(model) ?? findPrimaryKey(model, relationFilterDmmf);
            for (const record of records) {
              await tx[modelKey].update({
                where: applyActiveWriteFilter(
                  { ...(args.where ?? {}), [pkField]: record[pkField] },
                  deletedAtField,
                ),
                data,
              });
              if (cascadeHandler) {
                await cascadeHandler.cascadeSoftDelete(
                  tx,
                  model,
                  record[pkField],
                  data[deletedAtField] as Date,
                  0,
                  (childModel, callback) =>
                    runAuditLifecycle(
                      client,
                      options,
                      childModel,
                      'cascadeDelete',
                      'softDeleted',
                      callback,
                    ),
                );
              }
            }
            return { count: records.length };
          },
          'deleteMany',
        );

        getEventEmitter()?.emitSoftDeleted(
          new SoftDeletedEvent(
            model,
            args.where,
            data[deletedAtField] as Date,
            SoftDeleteContext.getActorId(),
            result.count,
          ),
        );
        return result;
      }

      if (cascadeHandler) {
        const pkField = cascadeHandler.findPrimaryKey(model);
        const activeWhere = applyActiveWriteFilter(args.where, deletedAtField);
        // Find records BEFORE soft-deleting them
        const toDelete = await (client as any)[modelKey].findMany({
          where: activeWhere,
          select: { [pkField]: true },
        });

        const result = await (client as any)[modelKey].updateMany({
          where: activeWhere,
          data,
        });

        for (const record of toDelete) {
          await cascadeHandler.cascadeSoftDelete(
            client,
            model,
            record[pkField],
            data[deletedAtField] as Date,
            0,
          );
        }

        getEventEmitter()?.emitSoftDeleted(
          new SoftDeletedEvent(
            model,
            args.where,
            data[deletedAtField] as Date,
            SoftDeleteContext.getActorId(),
            typeof (result as any).count === 'number' ? (result as any).count : undefined,
          ),
        );

        return result;
      }

      const result = await (client as any)[modelKey].updateMany({
        where: applyActiveWriteFilter(args.where, deletedAtField),
        data,
      });

      getEventEmitter()?.emitSoftDeleted(
        new SoftDeletedEvent(
          model,
          args.where,
          data[deletedAtField] as Date,
          SoftDeleteContext.getActorId(),
          typeof (result as any).count === 'number' ? (result as any).count : undefined,
        ),
      );

      return result;
    },

    findMany: createReadHandler('findMany'),
    findFirst: createReadHandler('findFirst'),
    findUnique: createReadHandler('findUnique'),
    findFirstOrThrow: createReadHandler('findFirstOrThrow'),
    findUniqueOrThrow: createReadHandler('findUniqueOrThrow'),
    count: createReadHandler('count'),
    aggregate: createReadHandler('aggregate'),
    groupBy: createReadHandler('groupBy'),
  };
}

/**
 * Creates a Prisma client extension that intercepts delete operations
 * (converting them to soft-delete updates) and read operations
 * (injecting deletedAt filters based on the current context).
 */
export function createPrismaSoftDeleteExtension(
  options: SoftDeleteExtensionOptions,
): ReturnType<typeof Prisma.defineExtension> {
  return Prisma.defineExtension((client) => {
    const handlers = _buildSoftDeleteQueryHandlers(options);

    return client.$extends({
      query: {
        $allModels: {
          async delete({ model, args, query }) {
            return handlers.delete({ model, args, query, client });
          },
          async deleteMany({ model, args, query }) {
            return handlers.deleteMany({ model, args, query, client });
          },
          async findMany({ model, args, query }) {
            return handlers.findMany({ model, args, query });
          },
          async findFirst({ model, args, query }) {
            return handlers.findFirst({ model, args, query });
          },
          async findUnique({ model, args, query }) {
            return handlers.findUnique({ model, args, query });
          },
          async findFirstOrThrow({ model, args, query }) {
            return handlers.findFirstOrThrow({ model, args, query });
          },
          async findUniqueOrThrow({ model, args, query }) {
            return handlers.findUniqueOrThrow({ model, args, query });
          },
          async count({ model, args, query }) {
            return handlers.count({ model, args, query });
          },
          async aggregate({ model, args, query }) {
            return handlers.aggregate({ model, args, query });
          },
          async groupBy({ model, args, query }) {
            return handlers.groupBy({ model, args, query });
          },
        },
      },
    });
  });
}
